"use client";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Select from "react-select";
import AlertDialog from "./AlertDialog";
import { useNavigate } from "react-router-dom";
const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const cls = (...c) => c.filter(Boolean).join(" ");

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const selectStyles = {
  control: (p, s) => ({
    ...p,
    backgroundColor: "#111827",
    borderColor: s.isFocused ? "#f97316" : "#374151",
    color: "#e5e7eb",
    boxShadow: s.isFocused ? "0 0 0 2px rgba(249,115,22,0.2)" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "34px",
    fontSize: "13px",
    cursor: "pointer",
    borderRadius: "0.375rem",
  }),
  input: (p) => ({ ...p, color: "#e5e7eb", margin: 0, padding: 0 }),
  singleValue: (p) => ({ ...p, color: "#f3f4f6", fontSize: "13px" }),
  placeholder: (p) => ({ ...p, color: "#6b7280", fontSize: "13px" }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isSelected
      ? "#92400e"
      : s.isFocused
        ? "#1f2937"
        : "#111827",
    color: s.isSelected ? "#fde68a" : "#e5e7eb",
    fontSize: "13px",
    cursor: "pointer",
    padding: "7px 12px",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: "#111827",
    borderRadius: "0.5rem",
    border: "1px solid #1f2937",
    zIndex: 9999,
  }),
  menuPortal: (p) => ({ ...p, zIndex: 9999 }),
};

const inputCls =
  "block w-full rounded-md bg-gray-900 border border-gray-700 text-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm py-2 px-3 transition-colors placeholder-gray-600";
const labelCls =
  "block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide";

const SOURCE_LABELS = {
  quickbooks: "QuickBooks",
  manual_csv: "CSV Import",
  manual_entry: "Manual",
};
const SOURCE_COLORS = {
  quickbooks: "text-blue-400 border-blue-700/50 bg-blue-900/20",
  manual_csv: "text-purple-400 border-purple-700/50 bg-purple-900/20",
  manual_entry: "text-gray-400 border-gray-700/50 bg-gray-800/40",
};

const SourceBadge = ({ source }) => (
  <span
    className={cls(
      "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border",
      SOURCE_COLORS[source] || SOURCE_COLORS.manual_entry,
    )}
  >
    {SOURCE_LABELS[source] || source}
  </span>
);

const VarianceBar = ({ percentUsed }) => {
  const pct = percentUsed == null ? 0 : Math.min(150, percentUsed);
  const color =
    percentUsed == null
      ? "#4b5563"
      : percentUsed >= 100
        ? "#ef4444"
        : percentUsed >= 85
          ? "#f59e0b"
          : "#10b981";
  return (
    <div className="w-full h-1.5 rounded-full bg-gray-800 overflow-hidden">
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Excel-style filterable + sortable column header, matching the pattern
// used in payroll.js. Clicking the label toggles sort (asc → desc →
// unsorted); clicking the funnel icon opens a checkbox-list dropdown of
// every distinct value in that column so the user can multi-select which
// values to show. Positioned as position: fixed at the trigger button's
// rect so it floats above the table regardless of scroll/row-count
// changes, exactly like the payroll picker's filter dropdown.
//
// Usage: one <FilterableSortableTh> per column, sharing one
// {openColumn, setOpenColumn} pair per table (so opening one closes any
// other), and one {filters, setFilters} state object keyed by column key
// (value is a Set of selected strings — empty/absent Set means "no
// filter", matching every row).
// ─────────────────────────────────────────────────────────────────────────
function FilterableSortableTh({
  colKey,
  label,
  align = "left",
  getOptions, // () => string[] — distinct values for this column, computed by the caller from its current row data
  sortState, // { key, dir } | { key: null }
  onSort, // (colKey) => void
  openColumn,
  setOpenColumn,
  filters,
  setFilters,
  className = "",
}) {
  const isOpen = openColumn === colKey;
  const filterValue = filters[colKey];
  const isFilterActive = filterValue instanceof Set && filterValue.size > 0;
  const isSorted = sortState.key === colKey;
  const [pos, setPos] = useState(null);

  const toggleOpen = (e) => {
    if (isOpen) {
      setOpenColumn(null);
      setPos(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, left: rect.left });
    setOpenColumn(colKey);
  };

  const toggleValue = (val) => {
    setFilters((prev) => {
      const current =
        prev[colKey] instanceof Set ? new Set(prev[colKey]) : new Set();
      if (current.has(val)) current.delete(val);
      else current.add(val);
      return { ...prev, [colKey]: current };
    });
  };

  const clearFilter = () => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[colKey];
      return next;
    });
  };

  // Close on outside click / underlying scroll, same behavior as payroll.js
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (!e.target.closest?.("[data-overhead-filter-dropdown]")) {
        setOpenColumn(null);
        setPos(null);
      }
    };
    const handleScroll = (e) => {
      if (e.target?.closest?.("[data-overhead-filter-dropdown]")) return;
      setOpenColumn(null);
      setPos(null);
    };
    document.addEventListener("mousedown", handleClick);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      window.removeEventListener("scroll", handleScroll, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const options = isOpen ? getOptions() : [];

  return (
    <th
      className={cls(
        "px-3 py-2 text-[11px] uppercase tracking-wider select-none relative",
        align === "right" ? "text-right" : "text-left",
        className,
      )}
    >
      <span className="inline-flex items-center gap-1">
        <button
          onClick={() => onSort(colKey)}
          className={cls(
            "inline-flex items-center gap-1 hover:text-orange-300 transition-colors",
            isFilterActive || isSorted ? "text-orange-400" : "text-gray-500",
          )}
          title="Sort"
        >
          {label}
          {isFilterActive && (
            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
          )}
          {isSorted && (
            <span className="text-orange-400">
              {sortState.dir === "asc" ? "↑" : "↓"}
            </span>
          )}
        </button>
        <button
          onClick={toggleOpen}
          title="Filter"
          className={cls(
            "inline-flex items-center justify-center w-5 h-5 rounded transition-colors",
            isFilterActive
              ? "bg-orange-500/20 text-orange-400"
              : "text-gray-500 hover:text-orange-300 hover:bg-gray-800",
          )}
        >
          <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
            <path d="M3 4a1 1 0 011-1h12a1 1 0 01.8 1.6l-4.8 6.4V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-4L3.2 4.6A1 1 0 013 4z" />
          </svg>
        </button>
      </span>

      {isOpen && pos && (
        <div
          data-overhead-filter-dropdown
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            zIndex: 200,
          }}
          className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl w-56 max-h-72 overflow-hidden flex flex-col normal-case font-normal tracking-normal"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-800">
            <span className="text-[10px] uppercase tracking-wider text-gray-500">
              {label}
            </span>
            {isFilterActive && (
              <button
                onClick={clearFilter}
                className="text-[11px] text-orange-400 hover:text-orange-300"
              >
                Clear
              </button>
            )}
          </div>
          <div className="overflow-y-auto py-1">
            {options.length === 0 ? (
              <div className="px-3 py-4 text-xs text-gray-600 text-center">
                No values
              </div>
            ) : (
              options.map((val) => {
                const selected =
                  filterValue instanceof Set && filterValue.has(val);
                return (
                  <label
                    key={val}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-800 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleValue(val)}
                      className="h-3.5 w-3.5 accent-orange-500 rounded bg-gray-900"
                    />
                    <span className="truncate">{val}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </th>
  );
}

// Toggle helper for a {key, dir} sort state: asc → desc → cleared.
function toggleSortState(setSortState, colKey) {
  setSortState((prev) => {
    if (prev.key !== colKey) return { key: colKey, dir: "asc" };
    if (prev.dir === "asc") return { key: colKey, dir: "desc" };
    return { key: null, dir: "asc" };
  });
}

// Applies a {colKey: Set} filters object to a row array, given a map of
// colKey -> getter(row) => displayValue. A column with no filter set (or
// an empty Set) passes every row through unfiltered.
function applyColumnFilters(rows, filters, getters) {
  return rows.filter((row) => {
    for (const [colKey, filterSet] of Object.entries(filters)) {
      if (!(filterSet instanceof Set) || filterSet.size === 0) continue;
      const getter = getters[colKey];
      if (!getter) continue;
      const val = getter(row);
      const display = val == null || val === "" ? "—" : String(val);
      if (!filterSet.has(display)) return false;
    }
    return true;
  });
}

// Sorts a row array by {key, dir}, using the same getters map as the
// filter (so "what a column shows" and "what it sorts by" always agree).
function applyColumnSort(rows, sortState, getters) {
  if (!sortState.key) return rows;
  const getter = getters[sortState.key];
  if (!getter) return rows;
  const factor = sortState.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = getter(a);
    const vb = getter(b);
    if (typeof va === "number" && typeof vb === "number")
      return (va - vb) * factor;
    return (
      String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
        numeric: true,
        sensitivity: "base",
      }) * factor
    );
  });
}

// ── Build a tree from the flat categories[] array for indented rendering.
// Returns an ordered, flattened list of { category, depth } pairs in
// parent-then-children (pre-order) sequence, so a plain <table> can render
// it as an indented tree without needing recursive JSX per row. ──
function flattenCategoryTree(categories) {
  const active = (categories || []).filter((c) => c.active !== false);
  const byParent = new Map(); // parentId (or "root") -> children[]
  for (const c of active) {
    const key = c.parentCategoryId ? String(c.parentCategoryId) : "root";
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(c);
  }
  // Keep a stable order: by accountNumber within each sibling group.
  for (const list of byParent.values()) {
    list.sort((a, b) =>
      String(a.accountNumber).localeCompare(
        String(b.accountNumber),
        undefined,
        { numeric: true },
      ),
    );
  }

  const out = [];
  function walk(parentKey, depth) {
    const children = byParent.get(parentKey) || [];
    for (const c of children) {
      out.push({ category: c, depth });
      walk(String(c._id), depth + 1);
    }
  }
  walk("root", 0);
  return out;
}

// ── Mapping helpers ──────────────────────────────────────────────────────
// A row is "mapped" when it has everything the backend needs to post it.
const isRowMapped = (s) => {
  if (!s) return false;
  return s.classification === "project_expense"
    ? !!s.projectId && !!s.linkedBillId
    : !!s.overheadBudgetId && !!s.overheadCategoryId;
};

function computeMappingStats(rows, rowState, keyFn) {
  let mapped = 0;
  let unmapped = 0;
  let excluded = 0;
  for (const r of rows) {
    const s = rowState[keyFn(r)];
    if (!s?.include) {
      excluded++;
      continue;
    }
    if (isRowMapped(s)) mapped++;
    else unmapped++;
  }
  return { mapped, unmapped, excluded, total: rows.length };
}

// Same as selectStyles but with a red border, for fields that still need a value.
const selectStylesUnmapped = {
  ...selectStyles,
  control: (p, s) => ({
    ...selectStyles.control(p, s),
    borderColor: s.isFocused ? "#f97316" : "#b91c1c",
    boxShadow: s.isFocused
      ? "0 0 0 2px rgba(249,115,22,0.2)"
      : "0 0 0 1px rgba(185,28,28,0.25)",
  }),
};

function MappingSummary({ stats, unmappedOnly, setUnmappedOnly }) {
  const { mapped, unmapped, excluded, total } = stats;
  if (!total) return null;
  const pct = (n) => `${(n / total) * 100}%`;
  const selected = mapped + unmapped;
  const mappedPct = selected > 0 ? Math.round((mapped / selected) * 100) : 0;

  return (
    <div className="rounded-lg border border-gray-800 bg-gray-800/30 p-3 mb-3">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="text-xs text-gray-400">
          <span className="text-gray-200 font-semibold">{mapped}</span> of{" "}
          <span className="text-gray-200 font-semibold">{selected}</span>{" "}
          selected rows have an account mapped
          {selected > 0 && (
            <span className="text-gray-500"> ({mappedPct}%)</span>
          )}
        </div>
        <button
          onClick={() => setUnmappedOnly((v) => !v)}
          disabled={unmapped === 0 && !unmappedOnly}
          className={cls(
            "px-2.5 py-1 text-[11px] rounded font-medium border transition-colors disabled:opacity-40",
            unmappedOnly
              ? "bg-red-600 border-red-500 text-white"
              : "bg-gray-800 border-gray-700 text-gray-400 hover:border-orange-500 hover:text-orange-300",
          )}
        >
          {unmappedOnly ? "Showing unmapped only ✕" : "Show unmapped only"}
        </button>
      </div>

      {/* Stacked bar */}
      <div className="w-full h-2 rounded-full bg-gray-800 overflow-hidden flex">
        <div
          className="h-full bg-emerald-500 transition-all"
          style={{ width: pct(mapped) }}
        />
        <div
          className="h-full bg-red-500 transition-all"
          style={{ width: pct(unmapped) }}
        />
        <div
          className="h-full bg-gray-600 transition-all"
          style={{ width: pct(excluded) }}
        />
      </div>

      <div className="flex items-center gap-4 mt-2 text-[11px]">
        <span className="inline-flex items-center gap-1.5 text-emerald-400">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          {mapped} mapped
        </span>
        <span
          className={cls(
            "inline-flex items-center gap-1.5",
            unmapped > 0 ? "text-red-400 font-semibold" : "text-gray-500",
          )}
        >
          <span className="w-2 h-2 rounded-full bg-red-500" />
          {unmapped} unmapped
        </span>
        <span className="inline-flex items-center gap-1.5 text-gray-500">
          <span className="w-2 h-2 rounded-full bg-gray-600" />
          {excluded} excluded
        </span>
      </div>
    </div>
  );
}

// ── Shared row-review list — used by both the QuickBooks and CSV tabs in
// the import modal. `rows` share an identical shape from either source. ──
function ExpenseReviewRows({
  rows,
  rowState,
  updateRow,
  rowKeyFn,
  allCategoryOptions,
  projectOptionsByRow,
  billOptionsByRow,
  searchProjects,
  loadBillsForProject,
  unmappedOnly = false,
}) {
  const visibleRows = unmappedOnly
    ? rows.filter((r) => {
        const s = rowState[rowKeyFn(r)];
        return s?.include && !isRowMapped(s);
      })
    : rows;

  if (!rows.length) {
    return (
      <div className="py-8 text-center text-gray-500 text-sm">
        Nothing new to import.
      </div>
    );
  }
  if (!visibleRows.length) {
    return (
      <div className="py-8 text-center text-emerald-400 text-sm">
        ✓ Every selected row is mapped.
      </div>
    );
  }
  return (
    <div className="max-h-[50vh] overflow-y-auto space-y-2">
      {visibleRows.map((r) => {
        const key = rowKeyFn(r);
        const s = rowState[key] || {};
        const mapped = isRowMapped(s);
        const unmapped = !!s.include && !mapped;
        return (
          <div
            key={key}
            className={cls(
              "rounded-lg border p-3 space-y-2 relative",
              !s.include
                ? "border-gray-800 bg-gray-900/40 opacity-60"
                : unmapped
                  ? "border-red-700/60 bg-red-950/10"
                  : "border-gray-700 bg-gray-800/40",
            )}
          >
            {unmapped && (
              <span className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg bg-red-500" />
            )}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={!!s.include}
                  onChange={(e) =>
                    updateRow(key, { include: e.target.checked })
                  }
                  className="mt-1 rounded border-gray-600 text-orange-400 focus:ring-orange-500 bg-gray-800"
                />
                <div>
                  <div className="text-sm text-gray-100">
                    {r.vendorName || "—"}{" "}
                    <span className="text-gray-500 text-xs">
                      · {new Date(r.txnDate).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {r.description || r.accountName || "No description"}
                  </div>
                  {r.accountName && (
                    <div className="text-[11px] text-gray-600 font-mono mt-0.5">
                      Account: {r.accountName}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="font-mono text-gray-200 text-sm whitespace-nowrap">
                  ${fmt(r.amount)}
                </span>
                {s.include &&
                  (mapped ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border text-emerald-400 border-emerald-700/50 bg-emerald-900/20">
                      ✓ Mapped
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border text-red-400 border-red-700/50 bg-red-900/20">
                      ⚠ Unmapped
                    </span>
                  ))}
              </div>
            </div>

            {s.include && (
              <div className="pl-6 space-y-2">
                <div className="flex gap-2 flex-wrap items-center">
                  <button
                    onClick={() =>
                      updateRow(key, {
                        classification: "overhead",
                        projectId: "",
                        linkedBillId: "",
                      })
                    }
                    className={cls(
                      "px-2.5 py-1 text-[11px] rounded font-medium border transition-colors",
                      s.classification === "overhead"
                        ? "bg-orange-600 border-orange-500 text-white"
                        : "bg-gray-800 border-gray-700 text-gray-400",
                    )}
                  >
                    Overhead
                  </button>
                  <button
                    onClick={() =>
                      updateRow(key, {
                        classification: "project_expense",
                        overheadBudgetId: "",
                        overheadCategoryId: "",
                      })
                    }
                    className={cls(
                      "px-2.5 py-1 text-[11px] rounded font-medium border transition-colors",
                      s.classification === "project_expense"
                        ? "bg-teal-600 border-teal-500 text-white"
                        : "bg-gray-800 border-gray-700 text-gray-400",
                    )}
                  >
                    Project Expense
                  </button>
                  {s.classification === "overhead" &&
                    (r.suggestedCategoryName ? (
                      <span className="text-[10px] text-gray-500">
                        Suggested: {r.suggestedCategoryName}
                        {r.matchConfidence != null &&
                          ` (${Math.round(r.matchConfidence * 100)}%)`}
                      </span>
                    ) : (
                      <span className="text-[10px] text-red-400/80">
                        No automatic match found
                      </span>
                    ))}
                </div>

                {s.classification === "overhead" ? (
                  <div className="max-w-sm">
                    <Select
                      options={allCategoryOptions}
                      value={
                        allCategoryOptions.find(
                          (o) =>
                            o.budgetId === s.overheadBudgetId &&
                            o.categoryId === s.overheadCategoryId,
                        ) || null
                      }
                      onChange={(sel) =>
                        updateRow(key, {
                          overheadBudgetId: sel?.budgetId || "",
                          overheadCategoryId: sel?.categoryId || "",
                        })
                      }
                      styles={mapped ? selectStyles : selectStylesUnmapped}
                      placeholder="Select overhead account…"
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-w-lg">
                    <Select
                      options={(projectOptionsByRow[key] || []).map((p) => ({
                        value: p._id,
                        label: `${p.projectNumber ? p.projectNumber + " — " : ""}${p.projectName}`,
                      }))}
                      value={
                        (projectOptionsByRow[key] || [])
                          .map((p) => ({ value: p._id, label: p.projectName }))
                          .find((o) => o.value === s.projectId) || null
                      }
                      onInputChange={(v) => {
                        if (v.length >= 2) searchProjects(key, v);
                      }}
                      onChange={(sel) => {
                        updateRow(key, {
                          projectId: sel?.value || "",
                          linkedBillId: "",
                        });
                        loadBillsForProject(key, sel?.value);
                      }}
                      styles={s.projectId ? selectStyles : selectStylesUnmapped}
                      placeholder="Search project…"
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                    <Select
                      options={(billOptionsByRow[key] || []).map((b) => ({
                        value: b._id,
                        label: `${b.billName} — $${fmt(b.total)} (${b.invoiceNumber})`,
                      }))}
                      value={
                        (billOptionsByRow[key] || [])
                          .map((b) => ({ value: b._id, label: b.billName }))
                          .find((o) => o.value === s.linkedBillId) || null
                      }
                      onChange={(sel) =>
                        updateRow(key, { linkedBillId: sel?.value || "" })
                      }
                      styles={
                        s.linkedBillId ? selectStyles : selectStylesUnmapped
                      }
                      placeholder={
                        s.projectId
                          ? "Select matching bill…"
                          : "Pick a project first"
                      }
                      isDisabled={!s.projectId}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Import Expenses modal — QuickBooks / CSV / Manual tabs ──────────────
function ImportExpensesModal({
  open,
  onClose,
  budgets,
  currentBudgetId,
  qbConnected,
  onImported,
  authH,
  showAlert,
}) {
  const [tab, setTab] = useState(qbConnected ? "quickbooks" : "csv");
  const [targetBudgetId, setTargetBudgetId] = useState(currentBudgetId || "");
  const [unmappedOnly, setUnmappedOnly] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [loadingQb, setLoadingQb] = useState(false);
  const [qbPreview, setQbPreview] = useState(null);

  const [csvFile, setCsvFile] = useState(null);
  const [loadingCsv, setLoadingCsv] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);

  const [manualForm, setManualForm] = useState({
    txnDate: new Date().toISOString().slice(0, 10),
    amount: "",
    description: "",
    vendorName: "",
    classification: "overhead",
    overheadBudgetId: currentBudgetId || "",
    overheadCategoryId: "",
    projectId: "",
    linkedBillId: "",
  });
  const [manualProjectOptions, setManualProjectOptions] = useState([]);
  const [manualBillOptions, setManualBillOptions] = useState([]);
  const [savingManual, setSavingManual] = useState(false);

  const [rowState, setRowState] = useState({});
  const [projectOptionsByRow, setProjectOptionsByRow] = useState({});
  const [billOptionsByRow, setBillOptionsByRow] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const qbRowKey = (r) => `qb:${r.qbPurchaseId}:${r.qbLineId}`;
  const csvRowKey = (r) => `csv:${csvPreview?.fileName}:${r.rowIndex}`;

  const targetBudget = (budgets || []).find(
    (b) => String(b._id) === String(targetBudgetId),
  );

  // Only the TARGET year's ledger is offered
  const allCategoryOptions = (budgets || [])
    .filter((b) => String(b._id) === String(targetBudgetId))
    .flatMap((b) => {
      const flat = flattenCategoryTree(b.categories || []);
      return flat.map(({ category: c, depth }) => ({
        value: `${b._id}::${c._id}`,
        budgetId: b._id,
        categoryId: c._id,
        label: `${"·  ".repeat(depth)}${c.accountNumber} — ${c.name} (FY${b.fiscalYear})`,
      }));
    });

  const resetPreviews = () => {
    setQbPreview(null);
    setCsvPreview(null);
    setRowState({});
    setProjectOptionsByRow({});
    setBillOptionsByRow({});
    setUnmappedOnly(false);
  };

  const changeTargetBudget = (id) => {
    setTargetBudgetId(id);
    resetPreviews(); // rows were mapped against the old year
    setManualForm((p) => ({
      ...p,
      overheadBudgetId: id,
      overheadCategoryId: "",
    }));
    const b = (budgets || []).find((x) => String(x._id) === String(id));
    if (b?.startDate) setYear(new Date(b.startDate).getFullYear());
  };

  useEffect(() => {
    if (open) {
      setTargetBudgetId(currentBudgetId || "");
      setManualForm((p) => ({
        ...p,
        overheadBudgetId: currentBudgetId || "",
        overheadCategoryId: "",
      }));
    } else {
      resetPreviews();
      setCsvFile(null);
      setTab(qbConnected ? "quickbooks" : "csv");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateRow = (key, patch) =>
    setRowState((p) => ({ ...p, [key]: { ...p[key], ...patch } }));

  const seedRowState = (rows, keyFn) => {
    const seeded = {};
    for (const r of rows) {
      if (r.alreadyImported || r.error) continue;
      seeded[keyFn(r)] = {
        include: true,
        classification: "overhead",
        overheadBudgetId: targetBudgetId || "",
        overheadCategoryId: r.suggestedCategoryId || "",
        projectId: "",
        linkedBillId: "",
      };
    }
    setRowState((p) => ({ ...p, ...seeded }));
  };

  const runQbPreview = async () => {
    if (!targetBudgetId) {
      showAlert("error", "No Fiscal Year", "Choose a fiscal year first.");
      return;
    }
    setLoadingQb(true);
    setQbPreview(null);
    try {
      const res = await axios.get(`${API}/overhead/quickbooks/preview`, {
        headers: authH(),
        params: { year, month, budgetId: targetBudgetId },
      });
      setQbPreview(res.data);
      seedRowState(res.data.rows, qbRowKey);
    } catch (err) {
      showAlert(
        "error",
        "Preview Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setLoadingQb(false);
    }
  };

  const runCsvPreview = async () => {
    if (!targetBudgetId) {
      showAlert("error", "No Fiscal Year", "Choose a fiscal year first.");
      return;
    }
    if (!csvFile) {
      showAlert("warning", "No File", "Choose a CSV file first.");
      return;
    }
    setLoadingCsv(true);
    setCsvPreview(null);
    try {
      const fd = new FormData();
      fd.append("budgetId", targetBudgetId);
      fd.append("file", csvFile);
      const res = await axios.post(`${API}/overhead/csv-preview`, fd, {
        headers: authH(),
      });
      setCsvPreview(res.data);
      seedRowState(res.data.rows, csvRowKey);
    } catch (err) {
      showAlert(
        "error",
        "CSV Import Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setLoadingCsv(false);
    }
  };

  const searchProjects = async (rowKey, search) => {
    try {
      const res = await axios.get(`${API}/overhead/lookup-projects`, {
        headers: authH(),
        params: { search },
      });
      setProjectOptionsByRow((p) => ({
        ...p,
        [rowKey]: res.data.projects || [],
      }));
    } catch {
      setProjectOptionsByRow((p) => ({ ...p, [rowKey]: [] }));
    }
  };

  const loadBillsForProject = async (rowKey, projectId) => {
    if (!projectId) {
      setBillOptionsByRow((p) => ({ ...p, [rowKey]: [] }));
      return;
    }
    try {
      const res = await axios.get(`${API}/overhead/quickbooks/project-bills`, {
        headers: authH(),
        params: { projectId },
      });
      setBillOptionsByRow((p) => ({ ...p, [rowKey]: res.data.bills || [] }));
    } catch {
      setBillOptionsByRow((p) => ({ ...p, [rowKey]: [] }));
    }
  };

  const buildSubmitLines = () => {
    const lines = [];

    for (const r of qbPreview?.rows || []) {
      if (r.alreadyImported) continue;
      const key = qbRowKey(r);
      const s = rowState[key];
      if (!s?.include) continue;
      lines.push({
        source: "quickbooks",
        qbPurchaseId: r.qbPurchaseId,
        qbLineId: r.qbLineId,
        qbSyncToken: r.qbSyncToken,
        accountRef: r.accountRef,
        txnDate: r.txnDate,
        amount: r.amount,
        description: r.description,
        vendorName: r.vendorName,
        paymentType: r.paymentType,
        classification: s.classification,
        overheadBudgetId:
          s.classification === "overhead" ? targetBudgetId : undefined,
        overheadCategoryId:
          s.classification === "overhead" ? s.overheadCategoryId : undefined,
        projectId:
          s.classification === "project_expense" ? s.projectId : undefined,
        linkedBillId:
          s.classification === "project_expense" ? s.linkedBillId : undefined,
      });
    }

    for (const r of csvPreview?.rows || []) {
      if (r.error) continue;
      const key = csvRowKey(r);
      const s = rowState[key];
      if (!s?.include) continue;
      lines.push({
        source: "manual_csv",
        sourceFileName: csvPreview.fileName,
        sourceRef: r.rowRef,
        accountName: r.accountName,
        txnDate: r.txnDate,
        amount: r.amount,
        description: r.description,
        vendorName: r.vendorName,
        classification: s.classification,
        overheadBudgetId:
          s.classification === "overhead" ? targetBudgetId : undefined,
        overheadCategoryId:
          s.classification === "overhead" ? s.overheadCategoryId : undefined,
        projectId:
          s.classification === "project_expense" ? s.projectId : undefined,
        linkedBillId:
          s.classification === "project_expense" ? s.linkedBillId : undefined,
      });
    }

    return lines;
  };

  const submitImport = async () => {
    if (!targetBudgetId) {
      showAlert(
        "error",
        "No Fiscal Year",
        "Choose a fiscal year to import into.",
      );
      return;
    }
    const lines = buildSubmitLines();
    const invalid = lines.filter((l) =>
      l.classification === "overhead"
        ? !l.overheadBudgetId || !l.overheadCategoryId
        : !l.projectId || !l.linkedBillId,
    );
    if (invalid.length) {
      showAlert(
        "error",
        "Incomplete Rows",
        `${invalid.length} row(s) are missing an account or a project/bill selection.`,
      );
      return;
    }
    if (!lines.length) {
      showAlert(
        "warning",
        "Nothing Selected",
        "Select at least one row to import.",
      );
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/overhead/import`,
        { lines },
        { headers: authH() },
      );
      showAlert(
        "success",
        "Imported",
        `${res.data.importedCount} transaction line(s) imported into ${
          targetBudget?.label || `FY${targetBudget?.fiscalYear}`
        }.${res.data.skippedCount ? ` ${res.data.skippedCount} skipped.` : ""}`,
      );
      onImported?.();
      onClose();
    } catch (err) {
      showAlert(
        "error",
        "Import Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const searchManualProjects = async (search) => {
    try {
      const res = await axios.get(`${API}/overhead/lookup-projects`, {
        headers: authH(),
        params: { search },
      });
      setManualProjectOptions(res.data.projects || []);
    } catch {
      setManualProjectOptions([]);
    }
  };
  const loadManualBills = async (projectId) => {
    if (!projectId) return setManualBillOptions([]);
    try {
      const res = await axios.get(`${API}/overhead/quickbooks/project-bills`, {
        headers: authH(),
        params: { projectId },
      });
      setManualBillOptions(res.data.bills || []);
    } catch {
      setManualBillOptions([]);
    }
  };

  const submitManualEntry = async () => {
    if (!manualForm.txnDate || !manualForm.amount) {
      showAlert("error", "Missing Fields", "Date and amount are required.");
      return;
    }
    if (
      manualForm.classification === "overhead"
        ? !manualForm.overheadBudgetId || !manualForm.overheadCategoryId
        : !manualForm.projectId || !manualForm.linkedBillId
    ) {
      showAlert(
        "error",
        "Missing Fields",
        manualForm.classification === "overhead"
          ? "Select a fiscal year and an overhead account."
          : "Select a project and a matching bill.",
      );
      return;
    }

    setSavingManual(true);
    try {
      await axios.post(`${API}/overhead/manual-entry`, manualForm, {
        headers: authH(),
      });
      showAlert("success", "Added", "Expense recorded.");
      onImported?.();
      setManualForm((p) => ({
        ...p,
        amount: "",
        description: "",
        vendorName: "",
        projectId: "",
        linkedBillId: "",
      }));
    } catch (err) {
      showAlert(
        "error",
        "Save Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setSavingManual(false);
    }
  };

  if (!open) return null;

  const budgetSelectOptions = (budgets || []).map((b) => ({
    value: b._id,
    label: b.label || `FY${b.fiscalYear}`,
  }));
  const budgetSelectValue = targetBudget
    ? {
        value: targetBudget._id,
        label: targetBudget.label || `FY${targetBudget.fiscalYear}`,
      }
    : null;

  const qbImportableRows = (qbPreview?.rows || []).filter(
    (r) => !r.alreadyImported,
  );
  const csvImportableRows = (csvPreview?.rows || []).filter((r) => !r.error);
  const totalSelected =
    qbImportableRows.filter((r) => rowState[qbRowKey(r)]?.include).length +
    csvImportableRows.filter((r) => rowState[csvRowKey(r)]?.include).length;

  const qbStats = computeMappingStats(qbImportableRows, rowState, qbRowKey);
  const csvStats = computeMappingStats(csvImportableRows, rowState, csvRowKey);
  const activeStats = tab === "quickbooks" ? qbStats : csvStats;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-5xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h3 className="text-base font-semibold text-white">
            Import Expenses
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        <div className="flex border-b border-gray-800 px-6">
          {[
            ...(qbConnected
              ? [{ key: "quickbooks", label: "QuickBooks" }]
              : []),
            { key: "csv", label: "CSV Upload" },
            { key: "manual", label: "Add Manually" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cls(
                "px-4 py-3 text-sm font-medium transition-colors relative",
                tab === t.key
                  ? "text-orange-400"
                  : "text-gray-500 hover:text-gray-300",
              )}
            >
              {t.label}
              {tab === t.key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-400" />
              )}
            </button>
          ))}
        </div>

        {/* ── Fiscal year selector (all tabs) ── */}
        <div className="px-6 py-3 border-b border-gray-800 flex items-center gap-3 flex-wrap bg-gray-800/20">
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
            Import into
          </span>
          <div className="min-w-[220px]">
            <Select
              options={budgetSelectOptions}
              value={budgetSelectValue}
              onChange={(sel) => changeTargetBudget(sel?.value || "")}
              styles={targetBudgetId ? selectStyles : selectStylesUnmapped}
              isSearchable={false}
              placeholder="Select fiscal year…"
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <span className="text-[11px] text-gray-600">
            Account suggestions and the account dropdown only use this year's
            ledger. Changing the year clears loaded rows.
          </span>
        </div>

        {tab === "quickbooks" && (
          <>
            <div className="px-6 py-4 border-b border-gray-800 flex items-end gap-3 flex-wrap">
              <div>
                <label className={labelCls}>Month</label>
                <select
                  className={inputCls}
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                >
                  {MONTH_NAMES.map((m, i) => (
                    <option key={m} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Year</label>
                <input
                  type="number"
                  className={inputCls}
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                />
              </div>
              <button
                onClick={runQbPreview}
                disabled={loadingQb}
                className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50"
              >
                {loadingQb ? "Loading…" : "Load Expenses"}
              </button>
              <p className="text-[11px] text-gray-600 ml-2">
                Only Purchase transactions posted to Expense-type accounts are
                shown.
              </p>
            </div>
            {qbPreview && (
              <div className="px-6 py-4">
                <MappingSummary
                  stats={qbStats}
                  unmappedOnly={unmappedOnly}
                  setUnmappedOnly={setUnmappedOnly}
                />
                <ExpenseReviewRows
                  rows={qbImportableRows}
                  rowState={rowState}
                  updateRow={updateRow}
                  rowKeyFn={qbRowKey}
                  allCategoryOptions={allCategoryOptions}
                  projectOptionsByRow={projectOptionsByRow}
                  billOptionsByRow={billOptionsByRow}
                  searchProjects={searchProjects}
                  loadBillsForProject={loadBillsForProject}
                  unmappedOnly={unmappedOnly}
                />
              </div>
            )}
          </>
        )}

        {tab === "csv" && (
          <>
            <div className="px-6 py-4 border-b border-gray-800 space-y-2">
              <label className={labelCls}>CSV File</label>
              <p className="text-[11px] text-gray-600 mb-1">
                Expected columns: date, amount, description, vendor, account
                (case-insensitive; extra columns are ignored).
              </p>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setCsvFile(e.target.files[0] || null)}
                  className="block text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-gray-800 file:text-gray-300 hover:file:bg-gray-700"
                />
                <button
                  onClick={runCsvPreview}
                  disabled={loadingCsv || !csvFile}
                  className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50"
                >
                  {loadingCsv ? "Parsing…" : "Load Rows"}
                </button>
              </div>
            </div>
            {csvPreview && (
              <div className="px-6 py-4">
                <MappingSummary
                  stats={csvStats}
                  unmappedOnly={unmappedOnly}
                  setUnmappedOnly={setUnmappedOnly}
                />
                <ExpenseReviewRows
                  rows={csvImportableRows}
                  rowState={rowState}
                  updateRow={updateRow}
                  rowKeyFn={csvRowKey}
                  allCategoryOptions={allCategoryOptions}
                  projectOptionsByRow={projectOptionsByRow}
                  billOptionsByRow={billOptionsByRow}
                  searchProjects={searchProjects}
                  loadBillsForProject={loadBillsForProject}
                  unmappedOnly={unmappedOnly}
                />
              </div>
            )}
          </>
        )}

        {tab === "manual" && (
          <div className="px-6 py-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Date</label>
                <input
                  type="date"
                  className={inputCls}
                  value={manualForm.txnDate}
                  onChange={(e) =>
                    setManualForm((p) => ({ ...p, txnDate: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  className={inputCls}
                  value={manualForm.amount}
                  onChange={(e) =>
                    setManualForm((p) => ({ ...p, amount: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Vendor</label>
                <input
                  className={inputCls}
                  value={manualForm.vendorName}
                  onChange={(e) =>
                    setManualForm((p) => ({ ...p, vendorName: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Description</label>
                <input
                  className={inputCls}
                  value={manualForm.description}
                  onChange={(e) =>
                    setManualForm((p) => ({
                      ...p,
                      description: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() =>
                  setManualForm((p) => ({
                    ...p,
                    classification: "overhead",
                    projectId: "",
                    linkedBillId: "",
                  }))
                }
                className={cls(
                  "px-2.5 py-1 text-[11px] rounded font-medium border transition-colors",
                  manualForm.classification === "overhead"
                    ? "bg-orange-600 border-orange-500 text-white"
                    : "bg-gray-800 border-gray-700 text-gray-400",
                )}
              >
                Overhead
              </button>
              <button
                onClick={() =>
                  setManualForm((p) => ({
                    ...p,
                    classification: "project_expense",
                    overheadCategoryId: "",
                  }))
                }
                className={cls(
                  "px-2.5 py-1 text-[11px] rounded font-medium border transition-colors",
                  manualForm.classification === "project_expense"
                    ? "bg-teal-600 border-teal-500 text-white"
                    : "bg-gray-800 border-gray-700 text-gray-400",
                )}
              >
                Project Expense
              </button>
            </div>

            {manualForm.classification === "overhead" ? (
              <div className="max-w-sm">
                <label className={labelCls}>Account</label>
                <Select
                  options={allCategoryOptions}
                  value={
                    allCategoryOptions.find(
                      (o) =>
                        o.budgetId === manualForm.overheadBudgetId &&
                        o.categoryId === manualForm.overheadCategoryId,
                    ) || null
                  }
                  onChange={(sel) =>
                    setManualForm((p) => ({
                      ...p,
                      overheadBudgetId: sel?.budgetId || targetBudgetId,
                      overheadCategoryId: sel?.categoryId || "",
                    }))
                  }
                  styles={selectStyles}
                  placeholder="Select overhead account…"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 max-w-lg">
                <div>
                  <label className={labelCls}>Project</label>
                  <Select
                    options={manualProjectOptions.map((p) => ({
                      value: p._id,
                      label: `${p.projectNumber ? p.projectNumber + " — " : ""}${p.projectName}`,
                    }))}
                    value={
                      manualProjectOptions
                        .map((p) => ({ value: p._id, label: p.projectName }))
                        .find((o) => o.value === manualForm.projectId) || null
                    }
                    onInputChange={(v) => {
                      if (v.length >= 2) searchManualProjects(v);
                    }}
                    onChange={(sel) => {
                      setManualForm((p) => ({
                        ...p,
                        projectId: sel?.value || "",
                        linkedBillId: "",
                      }));
                      loadManualBills(sel?.value);
                    }}
                    styles={selectStyles}
                    placeholder="Search project…"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
                <div>
                  <label className={labelCls}>Matching Bill</label>
                  <Select
                    options={manualBillOptions.map((b) => ({
                      value: b._id,
                      label: `${b.billName} — $${fmt(b.total)} (${b.invoiceNumber})`,
                    }))}
                    value={
                      manualBillOptions
                        .map((b) => ({ value: b._id, label: b.billName }))
                        .find((o) => o.value === manualForm.linkedBillId) ||
                      null
                    }
                    onChange={(sel) =>
                      setManualForm((p) => ({
                        ...p,
                        linkedBillId: sel?.value || "",
                      }))
                    }
                    styles={selectStyles}
                    placeholder={
                      manualForm.projectId
                        ? "Select matching bill…"
                        : "Pick a project first"
                    }
                    isDisabled={!manualForm.projectId}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={submitManualEntry}
                disabled={savingManual}
                className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-orange-500 hover:bg-orange-400 disabled:opacity-50"
              >
                {savingManual ? "Saving…" : "Add Expense"}
              </button>
            </div>
          </div>
        )}

        {tab !== "manual" && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
            <span className="text-xs text-gray-500">
              {qbPreview || csvPreview ? `${totalSelected} selected` : ""}
              {(qbPreview || csvPreview) && activeStats.unmapped > 0 && (
                <span className="text-red-400 ml-2">
                  · {activeStats.unmapped} still unmapped
                </span>
              )}
            </span>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-md text-sm font-medium text-gray-400 hover:text-gray-200"
              >
                Close
              </button>
              {(qbImportableRows.length > 0 ||
                csvImportableRows.length > 0) && (
                <button
                  onClick={submitImport}
                  disabled={submitting || totalSelected === 0}
                  className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-orange-500 hover:bg-orange-400 disabled:opacity-50"
                >
                  {submitting
                    ? "Importing…"
                    : `Import ${totalSelected || ""}`.trim()}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Edit Transaction modal — re-categorize, correct amount, void ────────
function EditTransactionModal({
  open,
  transactionId,
  budgets,
  onClose,
  onSaved,
  authH,
  showAlert,
}) {
  const [loading, setLoading] = useState(false);
  const [txn, setTxn] = useState(null);
  const [form, setForm] = useState(null);
  const [projectOptions, setProjectOptions] = useState([]);
  const [billOptions, setBillOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  const allCategoryOptions = (budgets || []).flatMap((b) => {
    const flat = flattenCategoryTree(b.categories || []);
    return flat.map(({ category: c, depth }) => ({
      value: `${b._id}::${c._id}`,
      budgetId: b._id,
      categoryId: c._id,
      label: `${"·  ".repeat(depth)}${c.accountNumber} — ${c.name} (FY${b.fiscalYear})`,
    }));
  });

  useEffect(() => {
    if (!open || !transactionId) {
      setTxn(null);
      setForm(null);
      return;
    }
    setLoading(true);
    axios
      .get(`${API}/overhead/transactions/${transactionId}`, {
        headers: authH(),
      })
      .then((res) => {
        const t = res.data.transaction;
        setTxn(t);
        setForm({
          txnDate: t.txnDate ? t.txnDate.slice(0, 10) : "",
          amount: t.amount,
          description: t.description || "",
          vendorName: t.vendorName || "",
          classification: t.classification,
          overheadBudgetId: t.overheadBudgetId || "",
          overheadCategoryId: t.overheadCategoryId || "",
          projectId: t.projectId?._id || t.projectId || "",
          linkedBillId: t.linkedBillId?._id || t.linkedBillId || "",
        });
        if (
          t.classification === "project_expense" &&
          (t.projectId?._id || t.projectId)
        ) {
          loadBills(t.projectId?._id || t.projectId);
        }
      })
      .catch((err) =>
        showAlert(
          "error",
          "Load Failed",
          err.response?.data?.message || err.message,
        ),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transactionId]);

  const searchProjects = async (search) => {
    try {
      const res = await axios.get(`${API}/overhead/lookup-projects`, {
        headers: authH(),
        params: { search },
      });
      setProjectOptions(res.data.projects || []);
    } catch {
      setProjectOptions([]);
    }
  };
  const loadBills = async (projectId) => {
    if (!projectId) return setBillOptions([]);
    try {
      const res = await axios.get(`${API}/overhead/quickbooks/project-bills`, {
        headers: authH(),
        params: { projectId },
      });
      setBillOptions(res.data.bills || []);
    } catch {
      setBillOptions([]);
    }
  };

  const save = async () => {
    if (
      form.classification === "overhead"
        ? !form.overheadBudgetId || !form.overheadCategoryId
        : !form.projectId || !form.linkedBillId
    ) {
      showAlert(
        "error",
        "Missing Fields",
        form.classification === "overhead"
          ? "Select an overhead account."
          : "Select a project and a matching bill.",
      );
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/overhead/transactions/${transactionId}`, form, {
        headers: authH(),
      });
      showAlert(
        "success",
        "Saved",
        "Transaction updated and locked against future auto-sync.",
      );
      onSaved?.();
      onClose();
    } catch (err) {
      showAlert(
        "error",
        "Save Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setSaving(false);
    }
  };

  const voidTxn = () => {
    showAlert(
      "warning",
      "Void Transaction",
      "Remove this from overhead totals? It stays on record but stops counting toward actuals.",
      {
        confirmText: "Void",
        cancelText: "Cancel",
        onConfirm: async () => {
          try {
            await axios.post(
              `${API}/overhead/transactions/${transactionId}/void`,
              {},
              { headers: authH() },
            );
            showAlert("success", "Voided", "Transaction voided.");
            onSaved?.();
            onClose();
          } catch (err) {
            showAlert(
              "error",
              "Void Failed",
              err.response?.data?.message || err.message,
            );
          }
        },
      },
    );
  };

  const dismissReview = async () => {
    try {
      await axios.post(
        `${API}/overhead/transactions/${transactionId}/dismiss-review`,
        {},
        { headers: authH() },
      );
      showAlert("success", "Dismissed", "Review flag cleared.");
      onSaved?.();
      onClose();
    } catch (err) {
      showAlert("error", "Failed", err.response?.data?.message || err.message);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h3 className="text-base font-semibold text-white">
            Edit Transaction
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {loading || !form ? (
          <div className="px-6 py-12 text-center text-gray-500 text-sm">
            Loading…
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {txn && (
              <div className="flex items-center gap-2 flex-wrap">
                <SourceBadge source={txn.source} />
                {txn.locked && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border text-amber-400 border-amber-700/50 bg-amber-900/20">
                    🔒 Locked — protected from auto-sync
                  </span>
                )}
                {txn.needsReview && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border text-red-400 border-red-700/50 bg-red-900/20">
                    Needs Review
                  </span>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Date</label>
                <input
                  type="date"
                  className={inputCls}
                  value={form.txnDate}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, txnDate: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  className={inputCls}
                  value={form.amount}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, amount: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Vendor</label>
                <input
                  className={inputCls}
                  value={form.vendorName}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, vendorName: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Description</label>
                <input
                  className={inputCls}
                  value={form.description}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() =>
                  setForm((p) => ({
                    ...p,
                    classification: "overhead",
                    projectId: "",
                    linkedBillId: "",
                  }))
                }
                className={cls(
                  "px-2.5 py-1 text-[11px] rounded font-medium border transition-colors",
                  form.classification === "overhead"
                    ? "bg-orange-600 border-orange-500 text-white"
                    : "bg-gray-800 border-gray-700 text-gray-400",
                )}
              >
                Overhead
              </button>
              <button
                onClick={() =>
                  setForm((p) => ({
                    ...p,
                    classification: "project_expense",
                    overheadBudgetId: "",
                    overheadCategoryId: "",
                  }))
                }
                className={cls(
                  "px-2.5 py-1 text-[11px] rounded font-medium border transition-colors",
                  form.classification === "project_expense"
                    ? "bg-teal-600 border-teal-500 text-white"
                    : "bg-gray-800 border-gray-700 text-gray-400",
                )}
              >
                Project Expense
              </button>
            </div>

            {form.classification === "overhead" ? (
              <Select
                options={allCategoryOptions}
                value={
                  allCategoryOptions.find(
                    (o) =>
                      o.budgetId === form.overheadBudgetId &&
                      o.categoryId === form.overheadCategoryId,
                  ) || null
                }
                onChange={(sel) =>
                  setForm((p) => ({
                    ...p,
                    overheadBudgetId: sel?.budgetId || "",
                    overheadCategoryId: sel?.categoryId || "",
                  }))
                }
                styles={selectStyles}
                placeholder="Select overhead account…"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Select
                  options={projectOptions.map((p) => ({
                    value: p._id,
                    label: `${p.projectNumber ? p.projectNumber + " — " : ""}${p.projectName}`,
                  }))}
                  value={
                    projectOptions
                      .map((p) => ({ value: p._id, label: p.projectName }))
                      .find((o) => o.value === form.projectId) || null
                  }
                  onInputChange={(v) => {
                    if (v.length >= 2) searchProjects(v);
                  }}
                  onChange={(sel) => {
                    setForm((p) => ({
                      ...p,
                      projectId: sel?.value || "",
                      linkedBillId: "",
                    }));
                    loadBills(sel?.value);
                  }}
                  styles={selectStyles}
                  placeholder="Search project…"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                <Select
                  options={billOptions.map((b) => ({
                    value: b._id,
                    label: `${b.billName} — $${fmt(b.total)} (${b.invoiceNumber})`,
                  }))}
                  value={
                    billOptions
                      .map((b) => ({ value: b._id, label: b.billName }))
                      .find((o) => o.value === form.linkedBillId) || null
                  }
                  onChange={(sel) =>
                    setForm((p) => ({ ...p, linkedBillId: sel?.value || "" }))
                  }
                  styles={selectStyles}
                  placeholder={
                    form.projectId
                      ? "Select matching bill…"
                      : "Pick a project first"
                  }
                  isDisabled={!form.projectId}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
          <div className="flex gap-2">
            {txn?.needsReview && (
              <button
                onClick={dismissReview}
                className="px-3 py-2 rounded-md text-xs font-medium text-gray-400 hover:text-gray-200 border border-gray-700"
              >
                Dismiss Review
              </button>
            )}
            <button
              onClick={voidTxn}
              className="px-3 py-2 rounded-md text-xs font-medium text-red-400 hover:text-red-300 border border-red-800/50"
            >
              Void
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md text-sm font-medium text-gray-400 hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving || loading}
              className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-orange-500 hover:bg-orange-400 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Post an existing bill directly to an overhead account ────────────────
function PostBillModal({
  open,
  onClose,
  budgets,
  currentBudgetId,
  onPosted,
  authH,
  showAlert,
}) {
  const [search, setSearch] = useState("");
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState("");
  const [categorySelection, setCategorySelection] = useState(null);
  const [posting, setPosting] = useState(false);

  const allCategoryOptions = (budgets || []).flatMap((b) => {
    const flat = flattenCategoryTree(b.categories || []);
    return flat.map(({ category: c, depth }) => ({
      value: `${b._id}::${c._id}`,
      budgetId: b._id,
      categoryId: c._id,
      label: `${"·  ".repeat(depth)}${c.accountNumber} — ${c.name} (FY${b.fiscalYear})`,
    }));
  });

  useEffect(() => {
    if (!open) {
      setSearch("");
      setBills([]);
      setSelectedBillId("");
      setCategorySelection(null);
    }
  }, [open]);

  const runSearch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/overhead/lookup-bills`, {
        headers: authH(),
        params: { search },
      });
      setBills(res.data.bills || []);
    } catch (err) {
      showAlert(
        "error",
        "Search Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const post = async () => {
    if (!selectedBillId || !categorySelection) {
      showAlert("error", "Missing Selection", "Choose a bill and an account.");
      return;
    }
    setPosting(true);
    try {
      await axios.post(
        `${API}/overhead/bills/${selectedBillId}/post`,
        {
          overheadBudgetId: categorySelection.budgetId,
          overheadCategoryId: categorySelection.categoryId,
        },
        { headers: authH() },
      );
      showAlert("success", "Posted", "Bill posted to the overhead account.");
      onPosted?.();
      onClose();
    } catch (err) {
      showAlert(
        "error",
        "Post Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setPosting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h3 className="text-base font-semibold text-white">
            Post Bill to Overhead Account
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 text-lg leading-none"
          >
            ✕
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className={labelCls}>Find Bill</label>
            <div className="flex gap-2">
              <input
                className={inputCls}
                placeholder="Search by bill name or invoice #…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
              />
              <button
                onClick={runSearch}
                disabled={loading}
                className="px-3 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50 whitespace-nowrap"
              >
                {loading ? "…" : "Search"}
              </button>
            </div>
          </div>

          {bills.length > 0 && (
            <div className="max-h-48 overflow-y-auto space-y-1 border border-gray-800 rounded-lg p-2">
              {bills.map((b) => (
                <label
                  key={b._id}
                  className={cls(
                    "flex items-center justify-between px-2 py-1.5 rounded text-xs cursor-pointer",
                    selectedBillId === b._id
                      ? "bg-orange-900/20 border border-orange-700/40"
                      : "hover:bg-gray-800/60",
                  )}
                >
                  <span className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={selectedBillId === b._id}
                      onChange={() => setSelectedBillId(b._id)}
                      className="text-orange-400 focus:ring-orange-500"
                    />
                    <span className="text-gray-200">{b.billName}</span>
                    <span className="text-gray-600">({b.invoiceNumber})</span>
                    {b.overheadCategoryId && (
                      <span className="text-[10px] text-amber-400">
                        already posted
                      </span>
                    )}
                  </span>
                  <span className="font-mono text-gray-400">
                    ${fmt(b.total)}
                  </span>
                </label>
              ))}
            </div>
          )}

          <div>
            <label className={labelCls}>Overhead Account</label>
            <Select
              options={allCategoryOptions}
              value={categorySelection}
              onChange={setCategorySelection}
              styles={selectStyles}
              placeholder="Select overhead account…"
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-400 hover:text-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={post}
            disabled={posting || !selectedBillId || !categorySelection}
            className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-orange-500 hover:bg-orange-400 disabled:opacity-50"
          >
            {posting ? "Posting…" : "Post to Account"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Transactions Browser — general filterable list across all budgets ───
function TransactionsBrowser({ authH, showAlert, onEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // Server-side query filters — these change what the API returns.
  const [queryFilters, setQueryFilters] = useState({
    classification: "",
    source: "",
    search: "",
  });
  // Client-side, per-column Excel-style filter/sort — applied on top of
  // whatever the current page of server results contains.
  const [columnFilters, setColumnFilters] = useState({});
  const [openColumn, setOpenColumn] = useState(null);
  const [sortState, setSortState] = useState({ key: null, dir: "asc" });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/overhead/transactions`, {
        headers: authH(),
        params: {
          page,
          limit: 50,
          classification: queryFilters.classification || undefined,
          source: queryFilters.source || undefined,
          search: queryFilters.search || undefined,
        },
      });
      setRows(res.data.transactions || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      showAlert(
        "error",
        "Load Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, queryFilters]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Column getters — the single source of truth for what each column
  // filters/sorts by AND displays, so a filter dropdown's checkbox values
  // always match what's shown in the cell.
  const colGetters = {
    txnDate: (r) =>
      r.txnDate ? new Date(r.txnDate).toLocaleDateString() : "—",
    vendorName: (r) => r.vendorName || "—",
    account: (r) =>
      r.classification === "overhead"
        ? r.categoryName || "Uncategorized"
        : `${r.projectId?.projectName || "Project"} · ${r.linkedBillId?.billName || "Bill"}`,
    source: (r) => SOURCE_LABELS[r.source] || r.source,
    amount: (r) => r.amount ?? 0,
    status: (r) =>
      r.voided
        ? "Voided"
        : r.needsReview
          ? "Needs Review"
          : r.locked
            ? "Locked"
            : "Open",
  };

  const distinctValuesFor = (key) => {
    const values = new Set(rows.map((r) => colGetters[key](r)));
    return Array.from(values).sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true }),
    );
  };

  const visibleRows = applyColumnSort(
    applyColumnFilters(rows, columnFilters, colGetters),
    sortState,
    colGetters,
  );

  const thProps = (colKey) => ({
    colKey,
    getOptions: () => distinctValuesFor(colKey),
    sortState,
    onSort: (k) => toggleSortState(setSortState, k),
    openColumn,
    setOpenColumn,
    filters: columnFilters,
    setFilters: setColumnFilters,
  });

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          className={inputCls + " max-w-xs"}
          placeholder="Search vendor or description…"
          value={queryFilters.search}
          onChange={(e) => {
            setQueryFilters((p) => ({ ...p, search: e.target.value }));
            setPage(1);
          }}
        />
        <select
          className={inputCls + " max-w-[160px]"}
          value={queryFilters.classification}
          onChange={(e) => {
            setQueryFilters((p) => ({ ...p, classification: e.target.value }));
            setPage(1);
          }}
        >
          <option value="">All Types</option>
          <option value="overhead">Overhead</option>
          <option value="project_expense">Project Expense</option>
        </select>
        <select
          className={inputCls + " max-w-[160px]"}
          value={queryFilters.source}
          onChange={(e) => {
            setQueryFilters((p) => ({ ...p, source: e.target.value }));
            setPage(1);
          }}
        >
          <option value="">All Sources</option>
          <option value="quickbooks">QuickBooks</option>
          <option value="manual_csv">CSV Import</option>
          <option value="manual_entry">Manual</option>
        </select>
        {Object.keys(columnFilters).some((k) => columnFilters[k]?.size > 0) && (
          <button
            onClick={() => setColumnFilters({})}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-amber-400 bg-amber-900/20 border border-amber-700/40 hover:bg-amber-900/40 transition-colors"
          >
            ✕ Clear column filters
          </button>
        )}
        <span className="text-xs text-gray-500 ml-auto">
          {visibleRows.length !== rows.length
            ? `${visibleRows.length} of ${rows.length} shown · `
            : ""}
          {total} total
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-800">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 bg-gray-800/40">
              <FilterableSortableTh label="Date" {...thProps("txnDate")} />
              <FilterableSortableTh
                label="Vendor / Description"
                {...thProps("vendorName")}
              />
              <FilterableSortableTh
                label="Account / Classification"
                {...thProps("account")}
              />
              <FilterableSortableTh label="Source" {...thProps("source")} />
              <FilterableSortableTh
                label="Amount"
                align="right"
                {...thProps("amount")}
              />
              <FilterableSortableTh label="Status" {...thProps("status")} />
              <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/60">
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-8 text-center text-gray-500 text-sm"
                >
                  Loading…
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-8 text-center text-gray-500 text-sm"
                >
                  {rows.length === 0
                    ? "No transactions found."
                    : "No rows match the current column filters."}
                </td>
              </tr>
            ) : (
              visibleRows.map((r) => (
                <tr key={r._id} className="hover:bg-gray-800/40">
                  <td className="px-3 py-2 text-gray-400 text-xs whitespace-nowrap">
                    {new Date(r.txnDate).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-gray-200 text-xs">
                      {r.vendorName || "—"}
                    </div>
                    <div className="text-gray-600 text-[11px]">
                      {r.description || "—"}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.classification === "overhead" ? (
                      <span className="text-orange-400 font-mono">
                        {r.categoryAccountNumber
                          ? `${r.categoryAccountNumber} — `
                          : ""}
                        {r.categoryName || "Uncategorized"}
                      </span>
                    ) : (
                      <span className="text-teal-400">
                        {r.projectId?.projectName || "Project"} ·{" "}
                        {r.linkedBillId?.billName || "Bill"}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <SourceBadge source={r.source} />
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-gray-200 text-xs">
                    ${fmt(r.amount)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 flex-wrap">
                      {r.locked && (
                        <span className="text-[10px] text-amber-400">🔒</span>
                      )}
                      {r.needsReview && (
                        <span className="text-[10px] px-1 py-0.5 rounded bg-red-900/30 text-red-400 border border-red-800/40">
                          Review
                        </span>
                      )}
                      {r.voided && (
                        <span className="text-[10px] px-1 py-0.5 rounded bg-gray-800 text-gray-500 border border-gray-700">
                          Voided
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => onEdit(r._id)}
                      className="text-[11px] text-orange-400 hover:text-orange-300 font-medium"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          className="px-3 py-1.5 rounded-md text-xs font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 border border-gray-700 disabled:opacity-40"
        >
          ← Prev
        </button>
        <span className="text-xs text-gray-500">Page {page}</span>
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={rows.length < 50}
          className="px-3 py-1.5 rounded-md text-xs font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 border border-gray-700 disabled:opacity-40"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

// ── Needs Review inbox — surfaces unlocked flagged rows ──────────────────
function NeedsReviewPanel({ authH, showAlert, onEdit, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/overhead/needs-review`, {
        headers: authH(),
      });
      setRows(res.data.transactions || []);
    } catch (err) {
      showAlert(
        "error",
        "Load Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  if (loading)
    return (
      <div className="py-6 text-center text-gray-500 text-sm">Loading…</div>
    );
  if (!rows.length)
    return (
      <div className="py-6 text-center text-gray-500 text-sm">
        Nothing needs review — all synced transactions are categorized.
      </div>
    );

  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div
          key={r._id}
          className="flex items-center justify-between rounded-lg border border-red-800/40 bg-red-950/10 px-4 py-3"
        >
          <div>
            <div className="text-sm text-gray-200">
              {r.vendorName || "—"}{" "}
              <span className="text-gray-500 text-xs">
                · {new Date(r.txnDate).toLocaleDateString()}
              </span>
            </div>
            <div className="text-xs text-gray-500">
              {r.description || r.accountName || "No description"} · $
              {fmt(r.amount)}
            </div>
            <div className="text-[11px] text-red-400 mt-0.5">
              {r.accountName
                ? `Source account "${r.accountName}" has no confident match`
                : "Needs an account"}
            </div>
          </div>
          <button
            onClick={() => onEdit(r._id)}
            className="px-3 py-1.5 text-xs font-semibold rounded-md bg-orange-600 hover:bg-orange-500 text-white"
          >
            Review
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Create Budget Year modal (blank or copied from an existing year) ─────
function CreateBudgetModal({
  open,
  onClose,
  budgets,
  onCreated,
  authH,
  showAlert,
}) {
  const [form, setForm] = useState({
    fiscalYear: new Date().getFullYear(),
    label: "",
    startDate: "",
    endDate: "",
    notes: "",
  });
  const [sourceId, setSourceId] = useState("");
  const [copyEstimates, setCopyEstimates] = useState(true);
  const [adjustPercent, setAdjustPercent] = useState(0);
  const [saving, setSaving] = useState(false);

  const sortedBudgets = [...(budgets || [])].sort(
    (a, b) => b.fiscalYear - a.fiscalYear,
  );
  const budgetOptions = sortedBudgets.map((b) => ({
    value: b._id,
    label: b.label || `FY${b.fiscalYear}`,
  }));

  useEffect(() => {
    if (!open) return;
    const latest = sortedBudgets[0];
    const nextYear = latest ? latest.fiscalYear + 1 : new Date().getFullYear();
    setForm({
      fiscalYear: nextYear,
      label: "",
      startDate: `${nextYear}-01-01`,
      endDate: `${nextYear}-12-31`,
      notes: "",
    });
    setSourceId(latest?._id || "");
    setCopyEstimates(true);
    setAdjustPercent(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const create = async () => {
    setSaving(true);
    try {
      let res;
      if (sourceId) {
        res = await axios.post(
          `${API}/overhead/budgets/${sourceId}/copy`,
          {
            ...form,
            copyEstimates,
            adjustPercent: Number(adjustPercent) || 0,
          },
          { headers: authH() },
        );
        showAlert(
          "success",
          "Created",
          `Budget created with ${res.data.copiedCount} accounts copied. Adjust them as needed.`,
        );
      } else {
        res = await axios.post(`${API}/overhead/budgets`, form, {
          headers: authH(),
        });
        showAlert("success", "Created", "Overhead budget created.");
      }
      onCreated?.(res.data.budget._id);
      onClose();
    } catch (err) {
      showAlert(
        "error",
        "Create Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h3 className="text-base font-semibold text-white">
            New Overhead Budget Year
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 text-lg leading-none"
          >
            ✕
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Fiscal Year</label>
              <input
                type="number"
                className={inputCls}
                value={form.fiscalYear}
                onChange={(e) => {
                  const y = Number(e.target.value);
                  setForm((p) => ({
                    ...p,
                    fiscalYear: y,
                    startDate: `${y}-01-01`,
                    endDate: `${y}-12-31`,
                  }));
                }}
              />
            </div>
            <div>
              <label className={labelCls}>Label (optional)</label>
              <input
                className={inputCls}
                value={form.label}
                placeholder={`FY${form.fiscalYear} Overhead`}
                onChange={(e) =>
                  setForm((p) => ({ ...p, label: e.target.value }))
                }
              />
            </div>
            <div>
              <label className={labelCls}>Start Date</label>
              <input
                type="date"
                className={inputCls}
                value={form.startDate}
                onChange={(e) =>
                  setForm((p) => ({ ...p, startDate: e.target.value }))
                }
              />
            </div>
            <div>
              <label className={labelCls}>End Date</label>
              <input
                type="date"
                className={inputCls}
                value={form.endDate}
                onChange={(e) =>
                  setForm((p) => ({ ...p, endDate: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="rounded-lg border border-gray-800 bg-gray-800/30 p-3 space-y-3">
            <div>
              <label className={labelCls}>Copy ledger from</label>
              <Select
                options={budgetOptions}
                value={budgetOptions.find((o) => o.value === sourceId) || null}
                onChange={(sel) => setSourceId(sel?.value || "")}
                styles={selectStyles}
                isClearable
                isSearchable={false}
                placeholder="Start blank (no copy)"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
            {sourceId && (
              <div className="grid grid-cols-2 gap-4 items-end">
                <label className="flex items-center gap-2 text-xs text-gray-300">
                  <input
                    type="checkbox"
                    checked={copyEstimates}
                    onChange={(e) => setCopyEstimates(e.target.checked)}
                    className="rounded border-gray-600 text-orange-400 focus:ring-orange-500 bg-gray-800"
                  />
                  Copy estimates
                </label>
                <div>
                  <label className={labelCls}>Adjust estimates (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    disabled={!copyEstimates}
                    className={inputCls + " disabled:opacity-40"}
                    value={adjustPercent}
                    onChange={(e) => setAdjustPercent(e.target.value)}
                    placeholder="e.g. 3 for +3%"
                  />
                </div>
              </div>
            )}
            <p className="text-[11px] text-gray-600">
              Accounts and hierarchy are cloned with new IDs, so the new year's
              actuals are completely independent of the old one.
            </p>
          </div>

          <div>
            <label className={labelCls}>Notes</label>
            <textarea
              rows={2}
              className={inputCls + " resize-none"}
              value={form.notes}
              onChange={(e) =>
                setForm((p) => ({ ...p, notes: e.target.value }))
              }
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-400 hover:text-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={create}
            disabled={saving}
            className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-orange-500 hover:bg-orange-400 disabled:opacity-50"
          >
            {saving
              ? "Creating…"
              : sourceId
                ? "Create & Copy Ledger"
                : "Create Budget"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function OverheadPage() {
  const authH = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const navigate = useNavigate();

  const [budgets, setBudgets] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedBudgetId, setSelectedBudgetId] = useState(null);
  const [budget, setBudget] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [qbConnected, setQbConnected] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPostBillModal, setShowPostBillModal] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [activeTab, setActiveTab] = useState("categories");

  // Excel-style filter/sort state for the Accounts table (see
  // accountsViewIsFlat below for how these interact with the tree view).
  const [categoryColumnFilters, setCategoryColumnFilters] = useState({});
  const [categorySortState, setCategorySortState] = useState({
    key: null,
    dir: "asc",
  });
  const [openAccountColumn, setOpenAccountColumn] = useState(null);

  const [showCreateBudget, setShowCreateBudget] = useState(false);
  const [newBudget, setNewBudget] = useState({
    fiscalYear: new Date().getFullYear(),
    label: "",
    startDate: `${new Date().getFullYear()}-01-01`,
    endDate: `${new Date().getFullYear()}-12-31`,
    notes: "",
  });
  const [creatingBudget, setCreatingBudget] = useState(false);

  const [showAddCategory, setShowAddCategory] = useState(false);
  // accountNumber is only meaningful for a TOP-LEVEL account (no parent).
  // accountSuffix is only meaningful for a SUBACCOUNT (has a parent) — the
  // parent's own accountNumber is always the mandatory prefix, enforced
  // server-side (see routes/overhead.js), so the form only ever collects
  // one or the other depending on whether parentCategoryId is set.
  const [newCategory, setNewCategory] = useState({
    accountNumber: "",
    accountSuffix: "",
    name: "",
    parentCategoryId: "",
    estimatedAmount: "",
    notes: "",
  });
  const [savingCategory, setSavingCategory] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);

  const [categoryDetail, setCategoryDetail] = useState(null);
  const [loadingCategoryDetail, setLoadingCategoryDetail] = useState(false);

  const [showPayrollImport, setShowPayrollImport] = useState(null);
  const [payrollCandidates, setPayrollCandidates] = useState([]);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [payrollSelected, setPayrollSelected] = useState(new Set());
  const [importingPayroll, setImportingPayroll] = useState(false);

  const [alert, setAlert] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });
  const showAlert = (type, title, message, opts = {}) =>
    setAlert({
      open: true,
      type,
      title,
      message,
      confirmText: opts.confirmText ?? "OK",
      cancelText: opts.cancelText,
      onConfirm:
        opts.onConfirm ?? (() => setAlert((p) => ({ ...p, open: false }))),
      onClose: () => setAlert((p) => ({ ...p, open: false })),
    });

  const fetchBudgets = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/overhead/budgets`, {
        headers: authH(),
      });
      setBudgets(res.data.budgets || []);
      if (!selectedBudgetId && res.data.budgets?.length) {
        setSelectedBudgetId(res.data.budgets[0]._id);
      }
    } catch (err) {
      showAlert(
        "error",
        "Load Error",
        err.response?.data?.message || err.message,
      );
    } finally {
      setLoadingList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchBudgets();
  }, [fetchBudgets]);

  useEffect(() => {
    axios
      .get(`${API}/overhead/quickbooks/status`, { headers: authH() })
      .then((res) => setQbConnected(!!res.data.connected))
      .catch(() => setQbConnected(false));
  }, []);

  const fetchBudgetDetail = useCallback(async (id) => {
    if (!id) return;
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/overhead/budgets/${id}`, {
        headers: authH(),
      });
      setBudget(res.data.budget);
    } catch (err) {
      showAlert(
        "error",
        "Load Error",
        err.response?.data?.message || err.message,
      );
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (selectedBudgetId) fetchBudgetDetail(selectedBudgetId);
  }, [selectedBudgetId, fetchBudgetDetail]);

  const refreshAll = () => {
    setRefreshKey((k) => k + 1);
    if (selectedBudgetId) fetchBudgetDetail(selectedBudgetId);
  };

  const createBudget = async () => {
    setCreatingBudget(true);
    try {
      const res = await axios.post(`${API}/overhead/budgets`, newBudget, {
        headers: authH(),
      });
      showAlert("success", "Created", "Overhead budget created.");
      setShowCreateBudget(false);
      setSelectedBudgetId(res.data.budget._id);
      fetchBudgets();
    } catch (err) {
      showAlert(
        "error",
        "Create Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setCreatingBudget(false);
    }
  };

  const openAddCategory = (parentCategoryId = "") => {
    setEditingCategoryId(null);
    setNewCategory({
      accountNumber: "",
      accountSuffix: "",
      name: "",
      parentCategoryId,
      estimatedAmount: "",
      notes: "",
    });
    setShowAddCategory(true);
  };
  const openEditCategory = (cat) => {
    setEditingCategoryId(cat._id);
    // When the category being edited has a parent, split its stored
    // accountNumber back into "everything after the parent's own number"
    // so the suffix field starts pre-filled with what's actually there
    // (rather than the whole code, which the field never edits directly).
    const parent = cat.parentCategoryId
      ? budget.categories.find((c) => c._id === cat.parentCategoryId)
      : null;
    const suffix =
      parent && cat.accountNumber.startsWith(parent.accountNumber)
        ? cat.accountNumber.slice(parent.accountNumber.length)
        : "";
    setNewCategory({
      accountNumber: parent ? "" : cat.accountNumber,
      accountSuffix: parent ? suffix : "",
      name: cat.name,
      parentCategoryId: cat.parentCategoryId || "",
      estimatedAmount: cat.estimatedAmount,
      notes: cat.notes || "",
    });
    setShowAddCategory(true);
  };

  const saveCategory = async () => {
    const hasParent = !!newCategory.parentCategoryId;
    if (hasParent && !newCategory.accountSuffix.trim()) {
      showAlert(
        "error",
        "Missing Suffix",
        "Enter the suffix to append to the parent account's code.",
      );
      return;
    }
    if (!hasParent && !newCategory.accountNumber.trim()) {
      showAlert(
        "error",
        "Missing Account Number",
        "Account number is required.",
      );
      return;
    }
    if (!newCategory.name.trim()) {
      showAlert("error", "Missing Name", "Category name is required.");
      return;
    }
    setSavingCategory(true);
    try {
      // Send ONLY the field that applies — accountSuffix for a subaccount,
      // accountNumber for a top-level one — matching the backend's
      // mutually-exclusive contract exactly, so there's never a stale
      // leftover value from switching parent selection mid-edit.
      const payload = {
        name: newCategory.name,
        parentCategoryId: newCategory.parentCategoryId || null,
        estimatedAmount: newCategory.estimatedAmount,
        notes: newCategory.notes,
        ...(hasParent
          ? { accountSuffix: newCategory.accountSuffix.trim() }
          : { accountNumber: newCategory.accountNumber.trim() }),
      };
      let res;
      if (editingCategoryId) {
        res = await axios.put(
          `${API}/overhead/budgets/${budget._id}/categories/${editingCategoryId}`,
          payload,
          { headers: authH() },
        );
      } else {
        res = await axios.post(
          `${API}/overhead/budgets/${budget._id}/categories`,
          payload,
          {
            headers: authH(),
          },
        );
      }
      if (res.data.childNumbersNowStale) {
        showAlert(
          "warning",
          "Saved — Check Subaccount Codes",
          "This account's code changed, but its existing subaccounts still show their OLD codes (they were fixed at creation time and don't auto-renumber). Edit them individually if you want their codes to reflect the new parent code.",
        );
      } else {
        showAlert("success", "Saved", "Account saved.");
      }
      setShowAddCategory(false);
      fetchBudgetDetail(budget._id);
    } catch (err) {
      showAlert(
        "error",
        "Save Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setSavingCategory(false);
    }
  };

  const archiveCategory = (cat) => {
    showAlert(
      "warning",
      "Remove Category",
      `Remove "${cat.name}"? If it (or any of its subcategories) is already linked to bills, payroll, or imported expenses, the whole subtree will be archived instead of deleted.`,
      {
        confirmText: "Remove",
        cancelText: "Cancel",
        onConfirm: async () => {
          setAlert((p) => ({ ...p, open: false }));
          try {
            const res = await axios.delete(
              `${API}/overhead/budgets/${budget._id}/categories/${cat._id}`,
              { headers: authH() },
            );
            showAlert(
              "success",
              res.data.archived ? "Archived" : "Removed",
              res.data.message || "Done.",
            );
            fetchBudgetDetail(budget._id);
          } catch (err) {
            showAlert(
              "error",
              "Failed",
              err.response?.data?.message || err.message,
            );
          }
        },
      },
    );
  };

  const openCategoryDetail = async (categoryId) => {
    setLoadingCategoryDetail(true);
    setCategoryDetail({ categoryId, loading: true });
    try {
      const res = await axios.get(
        `${API}/overhead/budgets/${budget._id}/category/${categoryId}/detail`,
        { headers: authH() },
      );
      setCategoryDetail(res.data);
    } catch (err) {
      showAlert(
        "error",
        "Load Failed",
        err.response?.data?.message || err.message,
      );
      setCategoryDetail(null);
    } finally {
      setLoadingCategoryDetail(false);
    }
  };

  const openPayrollImport = async (categoryId) => {
    setShowPayrollImport(categoryId);
    setPayrollSelected(new Set());
    setPayrollLoading(true);
    try {
      const res = await axios.get(
        `${API}/overhead/budgets/${budget._id}/payroll-candidates`,
        {
          headers: authH(),
        },
      );
      setPayrollCandidates(res.data.items || []);
    } catch (err) {
      showAlert(
        "error",
        "Load Failed",
        err.response?.data?.message || err.message,
      );
      setPayrollCandidates([]);
    } finally {
      setPayrollLoading(false);
    }
  };

  const togglePayrollLine = (id) =>
    setPayrollSelected((p) => {
      const next = new Set(p);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const submitPayrollImport = async () => {
    if (!payrollSelected.size) return;
    setImportingPayroll(true);
    try {
      const res = await axios.post(
        `${API}/overhead/budgets/${budget._id}/payroll-import`,
        {
          categoryId: showPayrollImport,
          payrollIds: Array.from(payrollSelected),
        },
        { headers: authH() },
      );
      showAlert(
        "success",
        "Imported",
        `${res.data.imported} payroll line(s) ported into this account.${
          res.data.skipped
            ? ` ${res.data.skipped} skipped (already tagged).`
            : ""
        }`,
      );
      setShowPayrollImport(null);
      fetchBudgetDetail(budget._id);
    } catch (err) {
      showAlert(
        "error",
        "Import Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setImportingPayroll(false);
    }
  };

  const budgetOptions = budgets.map((b) => ({
    value: b._id,
    label: b.label || `FY${b.fiscalYear}`,
  }));

  // Parent options for the add/edit category modal — every OTHER active
  // category on this budget, shown with indentation, excluding the
  // category being edited and its own descendants (to prevent cycles at
  // the UI layer, mirroring the backend guard).
  const parentOptionsForCategoryForm = budget
    ? flattenCategoryTree(budget.categories || [])
        .filter(({ category: c }) => c._id !== editingCategoryId)
        .map(({ category: c, depth }) => ({
          value: c._id,
          label: `${"·  ".repeat(depth)}${c.accountNumber} — ${c.name}`,
        }))
    : [];

  const treeCategoryRows = budget
    ? flattenCategoryTree(budget.categories || [])
    : [];

  // Column getters for the Accounts table, used for SORTING (need real
  // numbers so 90 doesn't sort before 100 alphabetically).
  const accountSortGetters = {
    accountNumber: (row) => row.category.accountNumber,
    name: (row) => row.category.name,
    estimatedAmount: (row) => row.category.estimatedAmount || 0,
    directActual: (row) => row.category.directActual || 0,
    estimatedTotal: (row) => row.category.estimatedTotal || 0,
    actualTotal: (row) => row.category.actualTotal || 0,
    variance: (row) => row.category.variance || 0,
  };
  // Column getters for FILTERING/display — currency columns are formatted
  // to the exact string shown in the dropdown and in the cell, so a
  // checkbox selection always matches what the user sees. Text columns
  // are identical to the sort getters.
  const CURRENCY_COLS = new Set([
    "estimatedAmount",
    "directActual",
    "estimatedTotal",
    "actualTotal",
    "variance",
  ]);
  const accountFilterGetters = Object.fromEntries(
    Object.entries(accountSortGetters).map(([key, getter]) => [
      key,
      CURRENCY_COLS.has(key) ? (row) => `$${fmt(getter(row))}` : getter,
    ]),
  );

  const accountFilteredRows = applyColumnFilters(
    treeCategoryRows,
    categoryColumnFilters,
    accountFilterGetters,
  );
  // A sort/filter flattens the view: indentation and the tree's natural
  // parent-before-children order only make sense when showing the WHOLE
  // tree unsorted. The moment either is active, show a flat, non-indented
  // list instead — mixing "sorted by amount" with "indented under its
  // parent" would be visually incoherent (a child could sort far from its
  // parent), so this switches views rather than trying to do both at once.
  const accountsViewIsFlat =
    categorySortState.key != null ||
    Object.values(categoryColumnFilters).some((s) => s?.size > 0);
  const displayedAccountRows = accountsViewIsFlat
    ? applyColumnSort(
        accountFilteredRows,
        categorySortState,
        accountSortGetters,
      )
    : treeCategoryRows;

  const distinctAccountValuesFor = (key) => {
    const values = new Set(
      treeCategoryRows.map((row) => accountFilterGetters[key](row)),
    );
    return Array.from(values).sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true }),
    );
  };
  const accountThProps = (colKey) => ({
    colKey,
    getOptions: () => distinctAccountValuesFor(colKey),
    sortState: categorySortState,
    onSort: (k) => toggleSortState(setCategorySortState, k),
    openColumn: openAccountColumn,
    setOpenColumn: setOpenAccountColumn,
    filters: categoryColumnFilters,
    setFilters: setCategoryColumnFilters,
  });

  return (
    <div className="mx-auto px-4 py-6 mt-8">
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        confirmText={alert.confirmText}
        cancelText={alert.cancelText}
        onClose={alert.onClose}
        onConfirm={alert.onConfirm}
      />
      <div className="bg-gray-900 rounded-xl border border-gray-800 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-800 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-white tracking-tight">
              Overhead Budget
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Chart-of-accounts style categories, with subcategory totals
              rolling up to their parent
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="min-w-[220px]">
              <Select
                options={budgetOptions}
                value={
                  budgetOptions.find((o) => o.value === selectedBudgetId) ||
                  null
                }
                onChange={(sel) => setSelectedBudgetId(sel?.value || null)}
                styles={selectStyles}
                placeholder={loadingList ? "Loading…" : "Select fiscal year…"}
                isSearchable={false}
              />
            </div>
            <button
              onClick={() =>
                navigate("/invoice-upload", {
                  state: {
                    destination: "overhead",
                    budgetId: selectedBudgetId,
                  },
                })
              }
              disabled={!selectedBudgetId}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold text-white bg-purple-600 hover:bg-purple-500 transition-colors shadow-sm disabled:opacity-40"
            >
              ⇧ Upload Bill
            </button>
            <button
              onClick={() => setShowPostBillModal(true)}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold text-white bg-teal-600 hover:bg-teal-500 transition-colors shadow-sm"
            >
              📎 Post a Bill
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold text-white bg-blue-600 hover:bg-blue-500 transition-colors shadow-sm"
            >
              ⇩ Import Expenses
            </button>
            <button
              onClick={() => setShowCreateBudget(true)}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold text-white bg-orange-500 hover:bg-orange-400 transition-colors shadow-sm"
            >
              + New Budget Year
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-800 px-6">
          {[
            { key: "categories", label: "Accounts" },
            { key: "transactions", label: "Transactions" },
            { key: "review", label: "Needs Review" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={cls(
                "px-4 py-3 text-sm font-medium transition-colors relative",
                activeTab === t.key
                  ? "text-orange-400"
                  : "text-gray-500 hover:text-gray-300",
              )}
            >
              {t.label}
              {activeTab === t.key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-400" />
              )}
            </button>
          ))}
        </div>

        {activeTab === "categories" &&
          (loadingDetail ? (
            <div className="px-6 py-12 text-center text-gray-500 text-sm">
              Loading…
            </div>
          ) : !budget ? (
            <div className="px-6 py-12 text-center text-gray-500 text-sm">
              No overhead budget yet. Create one to get started.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-6 border-b border-gray-800">
                {[
                  {
                    label: "Estimated",
                    value: `$${fmt(budget.totalEstimated)}`,
                  },
                  { label: "Actual", value: `$${fmt(budget.totalActual)}` },
                  {
                    label: "Variance",
                    value: `${budget.totalVariance >= 0 ? "" : "-"}$${fmt(Math.abs(budget.totalVariance))}`,
                    accent: budget.totalVariance >= 0 ? "#34d399" : "#ef4444",
                  },
                  {
                    label: "% Used",
                    value:
                      budget.totalEstimated > 0
                        ? `${((budget.totalActual / budget.totalEstimated) * 100).toFixed(1)}%`
                        : "—",
                  },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="rounded-lg bg-gray-800/50 border border-gray-800 p-4"
                  >
                    <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                      {s.label}
                    </p>
                    <p
                      className="text-xl font-semibold font-mono"
                      style={{ color: s.accent || "#f3f4f6" }}
                    >
                      {s.value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="px-6 py-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">
                  Accounts
                </h3>
                <button
                  onClick={() => openAddCategory("")}
                  className="px-3 py-1.5 text-xs font-medium rounded-md bg-gray-800 border border-gray-700 text-gray-300 hover:border-orange-500 hover:text-orange-300 transition-colors"
                >
                  + Add Account
                </button>
              </div>

              {accountsViewIsFlat && (
                <div className="px-6 pb-2 flex items-center gap-2">
                  <span className="text-[11px] text-amber-400">
                    Showing a flat, filtered/sorted list — clear filters and
                    sort to see the account tree again.
                  </span>
                  <button
                    onClick={() => {
                      setCategoryColumnFilters({});
                      setCategorySortState({ key: null, dir: "asc" });
                    }}
                    className="text-[11px] text-orange-400 hover:text-orange-300 font-medium"
                  >
                    Reset to tree view
                  </button>
                </div>
              )}

              <div className="overflow-x-auto px-6 pb-6">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800 bg-gray-800/40">
                      <FilterableSortableTh
                        label="#"
                        {...accountThProps("accountNumber")}
                      />
                      <FilterableSortableTh
                        label="Account"
                        {...accountThProps("name")}
                      />
                      <FilterableSortableTh
                        label="Own Est."
                        align="right"
                        {...accountThProps("estimatedAmount")}
                      />
                      <FilterableSortableTh
                        label="Own Actual"
                        align="right"
                        {...accountThProps("directActual")}
                      />
                      <FilterableSortableTh
                        label="Total Est."
                        align="right"
                        {...accountThProps("estimatedTotal")}
                      />
                      <FilterableSortableTh
                        label="Total Actual"
                        align="right"
                        {...accountThProps("actualTotal")}
                      />
                      <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-gray-500 w-28">
                        Usage
                      </th>
                      <FilterableSortableTh
                        label="Variance"
                        align="right"
                        {...accountThProps("variance")}
                      />
                      <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-gray-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/60">
                    {displayedAccountRows.map(
                      ({ category: cat, depth: treeDepth }) => {
                        // When the view is flattened by a sort/filter, don't
                        // show tree indentation — a sorted position no
                        // longer reflects the category's place in the
                        // hierarchy, so indenting it would misleadingly
                        // suggest it does.
                        const depth = accountsViewIsFlat ? 0 : treeDepth;
                        return (
                          <tr
                            key={cat._id}
                            className="hover:bg-gray-800/40 transition-colors"
                          >
                            <td className="px-3 py-3 font-mono text-gray-500 text-xs whitespace-nowrap">
                              {cat.accountNumber}
                            </td>
                            <td className="px-3 py-3">
                              <div
                                style={{ paddingLeft: `${depth * 18}px` }}
                                className="flex items-center gap-1.5"
                              >
                                {depth > 0 && (
                                  <span className="text-gray-600">└</span>
                                )}
                                <button
                                  onClick={() => openCategoryDetail(cat._id)}
                                  className="text-gray-100 font-medium hover:text-orange-300 text-left"
                                >
                                  {cat.name}
                                </button>
                                {!cat.isLeaf && (
                                  <span className="text-[9px] px-1 py-0.5 rounded bg-gray-800 text-gray-500 border border-gray-700">
                                    has subaccounts
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-3 text-right font-mono text-gray-400 text-xs">
                              ${fmt(cat.estimatedAmount)}
                            </td>
                            <td className="px-3 py-3 text-right font-mono text-gray-400 text-xs">
                              ${fmt(cat.directActual)}
                            </td>
                            <td className="px-3 py-3 text-right font-mono text-gray-300">
                              ${fmt(cat.estimatedTotal)}
                            </td>
                            <td className="px-3 py-3 text-right font-mono text-gray-100 font-semibold">
                              ${fmt(cat.actualTotal)}
                            </td>
                            <td className="px-3 py-3">
                              <VarianceBar percentUsed={cat.percentUsed} />
                              <div className="text-[10px] text-gray-500 mt-0.5">
                                {cat.percentUsed != null
                                  ? `${cat.percentUsed}%`
                                  : "—"}
                              </div>
                            </td>
                            <td
                              className="px-3 py-3 text-right font-mono"
                              style={{
                                color:
                                  cat.variance >= 0 ? "#34d399" : "#ef4444",
                              }}
                            >
                              {cat.variance >= 0 ? "" : "-"}$
                              {fmt(Math.abs(cat.variance))}
                            </td>
                            <td className="px-3 py-3">
                              <div className="flex items-center justify-end gap-2 flex-wrap">
                                <button
                                  onClick={() => openAddCategory(cat._id)}
                                  className="text-[11px] text-blue-400 hover:text-blue-300"
                                >
                                  + Sub
                                </button>
                                <button
                                  onClick={() => openPayrollImport(cat._id)}
                                  className="text-[11px] text-purple-400 hover:text-purple-300"
                                >
                                  + Payroll
                                </button>
                                <button
                                  onClick={() => openEditCategory(cat)}
                                  className="text-[11px] text-orange-400 hover:text-orange-300"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => archiveCategory(cat)}
                                  className="text-[11px] text-red-500 hover:text-red-400"
                                >
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      },
                    )}
                    {displayedAccountRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-3 py-8 text-center text-gray-500 text-sm"
                        >
                          {treeCategoryRows.length === 0
                            ? "No accounts yet. Add one to start tracking overhead."
                            : "No accounts match the current filters."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {treeCategoryRows.length > 0 && (
                  <p className="text-[11px] text-gray-600 mt-2">
                    "Own" columns show this account's direct spend only. "Total"
                    columns include every subaccount beneath it — a parent's
                    Total Actual always equals its Own Actual plus the sum of
                    all its subaccounts' Total Actuals.
                  </p>
                )}
              </div>
            </>
          ))}

        {activeTab === "transactions" && (
          <div className="p-6">
            <TransactionsBrowser
              authH={authH}
              showAlert={showAlert}
              onEdit={(id) => setEditingTransactionId(id)}
            />
          </div>
        )}

        {activeTab === "review" && (
          <div className="p-6">
            <NeedsReviewPanel
              authH={authH}
              showAlert={showAlert}
              onEdit={(id) => setEditingTransactionId(id)}
              refreshKey={refreshKey}
            />
          </div>
        )}
      </div>
      {/* ════ Create Budget Modal ════ */}
      <CreateBudgetModal
        open={showCreateBudget}
        onClose={() => setShowCreateBudget(false)}
        budgets={budgets}
        authH={authH}
        showAlert={showAlert}
        onCreated={(id) => {
          setSelectedBudgetId(id);
          fetchBudgets();
        }}
      />
      {/* ════ Add/Edit Category (Account) Modal ════ */}
      {showAddCategory && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
              <h3 className="text-base font-semibold text-white">
                {editingCategoryId ? "Edit Account" : "Add Overhead Account"}
              </h3>
              <button
                onClick={() => setShowAddCategory(false)}
                className="text-gray-500 hover:text-gray-300 text-lg leading-none"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className={labelCls}>
                  Parent Account{" "}
                  <span className="text-gray-600 font-normal normal-case">
                    (optional — leave blank for a top-level account)
                  </span>
                </label>
                <Select
                  options={parentOptionsForCategoryForm}
                  value={
                    parentOptionsForCategoryForm.find(
                      (o) => o.value === newCategory.parentCategoryId,
                    ) || null
                  }
                  onChange={(sel) =>
                    setNewCategory((p) => ({
                      ...p,
                      parentCategoryId: sel?.value || "",
                      // Switching parent invalidates whichever number field
                      // no longer applies, so it can't be silently sent
                      // stale — the user re-enters whichever field is now
                      // relevant.
                      accountNumber: "",
                      accountSuffix: "",
                    }))
                  }
                  styles={selectStyles}
                  placeholder="No parent (top-level account)"
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {newCategory.parentCategoryId ? (
                  (() => {
                    const parentCat = budget.categories.find(
                      (c) => c._id === newCategory.parentCategoryId,
                    );
                    return (
                      <div>
                        <label className={labelCls}>Account Code *</label>
                        <div className="flex items-stretch rounded-md border border-gray-700 bg-gray-900 overflow-hidden focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-orange-500">
                          <span className="flex items-center px-3 font-mono text-sm text-gray-500 bg-gray-800/60 border-r border-gray-700 select-none whitespace-nowrap">
                            {parentCat?.accountNumber || "…"}
                          </span>
                          <input
                            className="flex-1 min-w-0 bg-transparent text-gray-100 text-sm py-2 px-3 font-mono focus:outline-none placeholder-gray-600"
                            value={newCategory.accountSuffix}
                            onChange={(e) =>
                              setNewCategory((p) => ({
                                ...p,
                                accountSuffix: e.target.value,
                              }))
                            }
                            placeholder="A"
                            autoFocus
                          />
                        </div>
                        <p className="text-[10px] text-gray-600 mt-1">
                          Inherits the parent's code — only type the part that
                          comes after it.
                        </p>
                      </div>
                    );
                  })()
                ) : (
                  <div>
                    <label className={labelCls}>Account Number *</label>
                    <input
                      className={inputCls + " font-mono"}
                      value={newCategory.accountNumber}
                      onChange={(e) =>
                        setNewCategory((p) => ({
                          ...p,
                          accountNumber: e.target.value,
                        }))
                      }
                      placeholder="e.g. OHSD01"
                    />
                  </div>
                )}
                <div>
                  <label className={labelCls}>Name *</label>
                  <input
                    className={inputCls}
                    value={newCategory.name}
                    onChange={(e) =>
                      setNewCategory((p) => ({ ...p, name: e.target.value }))
                    }
                    placeholder="e.g. Vehicles, Fuel, Admin Salaries"
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>
                  Estimated Amount ($){" "}
                  <span className="text-gray-600 font-normal normal-case">
                    (this account's own estimate)
                  </span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  className={inputCls}
                  value={newCategory.estimatedAmount}
                  onChange={(e) =>
                    setNewCategory((p) => ({
                      ...p,
                      estimatedAmount: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Notes</label>
                <textarea
                  rows={2}
                  className={inputCls + " resize-none"}
                  value={newCategory.notes}
                  onChange={(e) =>
                    setNewCategory((p) => ({ ...p, notes: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-800">
              <button
                onClick={() => setShowAddCategory(false)}
                className="px-4 py-2 rounded-md text-sm font-medium text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={saveCategory}
                disabled={savingCategory}
                className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-orange-500 hover:bg-orange-400 disabled:opacity-50"
              >
                {savingCategory ? "Saving…" : "Save Account"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ════ Category Detail Drawer ════ */}
      {categoryDetail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-3xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
              <h3 className="text-base font-semibold text-white">
                <span className="font-mono text-gray-500">
                  {categoryDetail.category?.accountNumber}
                </span>{" "}
                {categoryDetail.category?.name || "Account"}
              </h3>
              <button
                onClick={() => setCategoryDetail(null)}
                className="text-gray-500 hover:text-gray-300 text-lg leading-none"
              >
                ✕
              </button>
            </div>
            {loadingCategoryDetail ? (
              <div className="px-6 py-12 text-center text-gray-500 text-sm">
                Loading…
              </div>
            ) : (
              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-3 gap-4">
                  <div className="rounded-lg bg-gray-800/50 border border-gray-800 p-4">
                    <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                      Bill Total (own)
                    </p>
                    <p className="text-lg font-mono text-gray-100">
                      ${fmt(categoryDetail.totals?.billTotal)}
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-800/50 border border-gray-800 p-4">
                    <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                      Payroll Total (own)
                    </p>
                    <p className="text-lg font-mono text-gray-100">
                      ${fmt(categoryDetail.totals?.payrollTotal)}
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-800/50 border border-gray-800 p-4">
                    <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                      Imported Total (own)
                    </p>
                    <p className="text-lg font-mono text-gray-100">
                      ${fmt(categoryDetail.totals?.importedTotal)}
                    </p>
                  </div>
                </div>

                {categoryDetail.childCategories?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                      Subaccounts ({categoryDetail.childCategories.length})
                    </h4>
                    <div className="space-y-1">
                      {categoryDetail.childCategories.map((c) => (
                        <button
                          key={c._id}
                          onClick={() => openCategoryDetail(c._id)}
                          className="w-full flex justify-between px-3 py-2 rounded bg-gray-800/40 text-xs hover:bg-gray-800/70 text-left transition-colors"
                        >
                          <span className="text-gray-300 font-mono">
                            {c.accountNumber} — {c.name}
                          </span>
                          <span className="text-gray-400 font-mono">
                            ${fmt(c.actualTotal)} / ${fmt(c.estimatedTotal)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Bills Posted Directly ({categoryDetail.bills?.length || 0})
                  </h4>
                  {categoryDetail.bills?.length ? (
                    <div className="space-y-1">
                      {categoryDetail.bills.map((b) => (
                        <div
                          key={b._id}
                          className="flex justify-between px-3 py-2 rounded bg-gray-800/40 text-xs"
                        >
                          <span className="text-gray-300">
                            {b.billName} ({b.invoiceNumber})
                          </span>
                          <span className="text-gray-400 font-mono">
                            ${fmt(b.netAmount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600 italic">
                      No bills posted directly to this account.
                    </p>
                  )}
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Payroll Lines ({categoryDetail.payrollLines?.length || 0})
                  </h4>
                  {categoryDetail.payrollLines?.length ? (
                    <div className="space-y-1">
                      {categoryDetail.payrollLines.map((l) => (
                        <div
                          key={l._id}
                          className="flex justify-between px-3 py-2 rounded bg-gray-800/40 text-xs"
                        >
                          <span className="text-gray-300">
                            {l.displayName} —{" "}
                            {l.payPeriodStart
                              ? new Date(l.payPeriodStart).toLocaleDateString()
                              : "—"}
                          </span>
                          <span className="text-gray-400 font-mono">
                            ${fmt(l.totalPay)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600 italic">
                      No payroll ported yet.
                    </p>
                  )}
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Imported Transactions (
                    {categoryDetail.importedTransactions?.length || 0})
                  </h4>
                  {categoryDetail.importedTransactions?.length ? (
                    <div className="space-y-1">
                      {categoryDetail.importedTransactions.map((t) => (
                        <div
                          key={t._id}
                          className="flex justify-between px-3 py-2 rounded bg-gray-800/40 text-xs items-center"
                        >
                          <span className="text-gray-300 flex items-center gap-2">
                            {t.vendorName || t.description || "—"}
                            <SourceBadge source={t.source} />
                          </span>
                          <span className="text-gray-400 font-mono">
                            ${fmt(t.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600 italic">
                      No imported transactions yet.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* ════ Payroll Import Picker ════ */}
      {showPayrollImport && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
              <h3 className="text-base font-semibold text-white">
                Import Salary Payroll into Overhead
              </h3>
              <button
                onClick={() => setShowPayrollImport(null)}
                className="text-gray-500 hover:text-gray-300 text-lg leading-none"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <p className="text-xs text-gray-500 mb-3">
                Only salary payroll lines within this budget's fiscal year, not
                already ported to any overhead account, are shown.
              </p>
              {payrollLoading ? (
                <div className="py-8 text-center text-gray-500 text-sm">
                  Loading…
                </div>
              ) : payrollCandidates.length === 0 ? (
                <div className="py-8 text-center text-gray-500 text-sm">
                  No unimported salary payroll lines found for this period.
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto space-y-1">
                  {payrollCandidates.map((l) => {
                    const checked = payrollSelected.has(l._id);
                    return (
                      <label
                        key={l._id}
                        className={cls(
                          "flex items-center justify-between px-3 py-2 rounded text-xs cursor-pointer transition-colors",
                          checked
                            ? "bg-orange-900/20 border border-orange-700/40"
                            : "bg-gray-800/40 border border-transparent",
                        )}
                      >
                        <span className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePayrollLine(l._id)}
                            className="rounded border-gray-600 text-orange-400 focus:ring-orange-500 bg-gray-800"
                          />
                          <span className="text-gray-200">{l.worker}</span>
                          <span className="text-gray-600">
                            {l.payPeriodStart
                              ? new Date(l.payPeriodStart).toLocaleDateString()
                              : "—"}
                          </span>
                        </span>
                        <span className="font-mono text-gray-300">
                          ${fmt(l.totalPay)}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
              <span className="text-xs text-gray-500">
                {payrollSelected.size} selected · $
                {fmt(
                  payrollCandidates
                    .filter((l) => payrollSelected.has(l._id))
                    .reduce((s, l) => s + (l.totalPay || 0), 0),
                )}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowPayrollImport(null)}
                  className="px-4 py-2 rounded-md text-sm font-medium text-gray-400 hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={submitPayrollImport}
                  disabled={payrollSelected.size === 0 || importingPayroll}
                  className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-orange-500 hover:bg-orange-400 disabled:opacity-50"
                >
                  {importingPayroll
                    ? "Importing…"
                    : `Import ${payrollSelected.size || ""}`.trim()}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ════ Post Bill Modal ════ */}
      <PostBillModal
        open={showPostBillModal}
        onClose={() => setShowPostBillModal(false)}
        budgets={budgets}
        currentBudgetId={selectedBudgetId}
        onPosted={refreshAll}
        authH={authH}
        showAlert={showAlert}
      />
      {/* ════ Import Expenses Modal (QB / CSV / Manual) ════ */}
      <ImportExpensesModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        budgets={budgets}
        currentBudgetId={selectedBudgetId}
        qbConnected={qbConnected}
        onImported={refreshAll}
        authH={authH}
        showAlert={showAlert}
      />
      {/* ════ Edit Transaction Modal ════ */}
      <EditTransactionModal
        open={!!editingTransactionId}
        transactionId={editingTransactionId}
        budgets={budgets}
        onClose={() => setEditingTransactionId(null)}
        onSaved={refreshAll}
        authH={authH}
        showAlert={showAlert}
      />
    </div>
  );
}
